<script>
import useSharedData from "@/use/SharedData.js";
import useValidation from "@/use/Validation.js";
import useTools from "@/use/Tools.js";
import {computed, onMounted, ref} from "vue";
import {useRecruitmentRequestStore} from "@/stores/Request/RecruitmentRequest.js";
import useSharedMethod from "@/use/SharedMethod.js";
import axios from "axios";
import {useSecurityStore} from "@/stores/Security";

export default {
    name: "RecruitmentRequestForm",

    setup() {
        const {mxRules} = useValidation();
        const {loading, dialog, inputColor, backgroundColor} = useSharedData();
        const {$route, $refs} = useTools();
        const {getBack} = useSharedMethod();

        const securityStore = useSecurityStore();
        const recruitmentRequestStore = useRecruitmentRequestStore();

        const recruitmentRequest = ref({});

        const readonly = computed(() => recruitmentRequest.value.state === 'CLOSED')

        function findById(id) {
            loading.value = true;
            axios.get(`/recruitmentRequests/${id}`).then((response) => {
                recruitmentRequest.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }

        function apply() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/recruitmentRequests/applyFilter', recruitmentRequest.value).then((response) => {
                    recruitmentRequestStore.updateRecruitmentRequest(response.data);
                    securityStore.setRefresh();
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }


        onMounted(() => findById($route.params.id));

        return {
            recruitmentRequest,
            inputColor,
            backgroundColor,
            mxRules,
            loading,
            dialog,
            readonly,
            apply,
            getBack,
        };
    },
};
</script>

<template>
    <v-dialog
        v-model="dialog"
        persistent
        max-width="750"
        scrollable
    >
        <v-card>
            <w-toolbar color="purple" :title="recruitmentRequest.occupationName"/>
            <v-card-text style="background-color: #FAFAFA;">
                <v-form ref="form" lazy-validation>
                    <v-tabs
                        class="tabs"
                        color="teal"
                        :background-color="backgroundColor"
                        vertical
                        slider-size="7"
                    >
                        <v-tab>
                            <v-icon left>mdi-filter-cog</v-icon>
                            {{ $t('common.filter') }}
                        </v-tab>
                        <v-tab>
                            <v-icon left>mdi-file-cog</v-icon>
                            {{ $t('common.incomplete') }}
                        </v-tab>

                        <v-tab-item>
                            <v-simple-table dense>
                                <template v-slot:default>
                                    <tbody>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('common.sex') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-radio-group
                                                v-model="recruitmentRequest.sexFiltering"
                                                row
                                            >
                                                <v-radio
                                                    :value="null"
                                                    :label="$t('common.all')"
                                                    color="teal"
                                                    :readonly="readonly"
                                                />
                                                <v-radio
                                                    value="M"
                                                    :label="$t('common.man')"
                                                    color="teal"
                                                    :readonly="readonly"
                                                />
                                                <v-radio
                                                    value="F"
                                                    :label="$t('common.woman')"
                                                    color="teal"
                                                    :readonly="readonly"
                                                />
                                            </v-radio-group>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('common.age') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-row dense class="ma-1">
                                                <v-col cols="6">
                                                    <w-text-field
                                                        v-model="recruitmentRequest.startAgeFiltering"
                                                        type="number"
                                                        :label="$t('common.from')"
                                                        :readonly="readonly"
                                                    />
                                                </v-col>
                                                <v-col cols="6">
                                                    <w-text-field
                                                        v-model="recruitmentRequest.endAgeFiltering"
                                                        type="number"
                                                        :label="$t('common.to')"
                                                        :readonly="readonly"
                                                    />
                                                </v-col>
                                            </v-row>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('common.country_state') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-row dense class="ma-1">
                                                <v-col cols="12">
                                                    <w-country-state-autocomplete
                                                        v-model="recruitmentRequest.stateFilteringIds"
                                                        multiple
                                                        :readonly="readonly"
                                                    />
                                                </v-col>
                                            </v-row>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('data_select.RECRUITMENT_CV_TECH_SPECIALITY') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-row dense class="ma-1">
                                                <v-col cols="12">
                                                    <w-server-autocomplete
                                                        v-model="recruitmentRequest.specialityFilteringIds"
                                                        multiple
                                                        api="dataSelects/type/RECRUITMENT_CV_TECH_SPECIALITY"
                                                        :readonly="readonly"
                                                    />
                                                </v-col>
                                            </v-row>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('recruitment_request.experience') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-row dense class="ma-1">
                                                <v-col cols="12">
                                                    <w-text-field
                                                        v-model="recruitmentRequest.yearOfExperienceFiltering"
                                                        type="number"
                                                        :readonly="readonly"
                                                    />
                                                </v-col>
                                            </v-row>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('recruitment_request.married') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-checkbox
                                                class="mt-n1"
                                                color="teal"
                                                v-model="recruitmentRequest.marriedFiltering"
                                                hide-details
                                                :readonly="readonly"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('recruitment_request.has_children') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-checkbox
                                                class="mt-n1"
                                                color="teal"
                                                v-model="recruitmentRequest.hasChildrenFiltering"
                                                hide-details
                                                :readonly="readonly"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('recruitment_request.has_driver_license') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-checkbox
                                                class="mt-n1"
                                                color="teal"
                                                v-model="recruitmentRequest.hasDrivingLicenseFiltering"
                                                hide-details
                                                :readonly="readonly"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('recruitment_request.has_passport') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-checkbox
                                                class="mt-n1"
                                                color="teal"
                                                v-model="recruitmentRequest.hasValidPassportFiltering"
                                                hide-details
                                                :readonly="readonly"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('recruitment_request.has_vehicle') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-checkbox
                                                class="mt-n1"
                                                color="teal"
                                                v-model="recruitmentRequest.hasCarFiltering"
                                                hide-details
                                                :readonly="readonly"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('recruitment_request.free_military_service') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-checkbox
                                                class="mt-n1"
                                                color="teal"
                                                v-model="recruitmentRequest.freeMilitaryServiceFiltering"
                                                hide-details
                                                :readonly="readonly"
                                            />
                                        </td>
                                    </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </v-tab-item>
                        <v-tab-item>
                            <v-simple-table dense>
                                <template v-slot:default>
                                    <tbody>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('common.sex') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-checkbox
                                                class="mt-n1"
                                                color="teal"
                                                v-model="recruitmentRequest.checkSexCompleted"
                                                hide-details
                                                :readonly="readonly"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('common.last_name') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-checkbox
                                                class="mt-n1"
                                                color="teal"
                                                v-model="recruitmentRequest.checkLastNameCompleted"
                                                hide-details
                                                :readonly="readonly"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('common.first_name') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-checkbox
                                                class="mt-n1"
                                                color="teal"
                                                v-model="recruitmentRequest.checkFirstNameCompleted"
                                                hide-details
                                                :readonly="readonly"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('common.birth_day') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-checkbox
                                                class="mt-n1"
                                                color="teal"
                                                v-model="recruitmentRequest.checkBirthDateCompleted"
                                                hide-details
                                                :readonly="readonly"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('common.age') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-checkbox
                                                class="mt-n1"
                                                color="teal"
                                                v-model="recruitmentRequest.checkAgeCompleted"
                                                hide-details
                                                :readonly="readonly"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('common.phone') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-checkbox
                                                class="mt-n1"
                                                color="teal"
                                                v-model="recruitmentRequest.checkPhoneCompleted"
                                                hide-details
                                                :readonly="readonly"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('common.email') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-checkbox
                                                class="mt-n1"
                                                color="teal"
                                                v-model="recruitmentRequest.checkEmailCompleted"
                                                hide-details
                                                :readonly="readonly"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('common.address') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-checkbox
                                                class="mt-n1"
                                                color="teal"
                                                v-model="recruitmentRequest.checkAddressCompleted"
                                                hide-details
                                                :readonly="readonly"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('common.country_state') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-checkbox
                                                class="mt-n1"
                                                color="teal"
                                                v-model="recruitmentRequest.checkWilayaCompleted"
                                                hide-details
                                                :readonly="readonly"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('data_select.RECRUITMENT_CV_TECH_SPECIALITY') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-checkbox
                                                class="mt-n1"
                                                color="teal"
                                                v-model="recruitmentRequest.checkSpecialityCompleted"
                                                hide-details
                                                :readonly="readonly"
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td class="text-right" style="width: 30%">
                                            <strong>{{ $t('recruitment_request.experience') }}</strong>
                                        </td>
                                        <td class="text-left" style="width: 70%">
                                            <v-checkbox
                                                class="mt-n1"
                                                color="teal"
                                                v-model="recruitmentRequest.checkYearOfExperienceCompleted"
                                                hide-details
                                                :readonly="readonly"
                                            />
                                        </td>
                                    </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </v-tab-item>
                    </v-tabs>
                </v-form>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn
                    v-if="!readonly"
                    color="purple"
                    icon="mdi-filter"
                    :loading="loading"
                    :text="$t('common.to_filter')"
                    @press="apply"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<style lang="scss" scoped>
div.tabs [role="tab"] {
    justify-content: flex-start;
}
</style>